<template>
  <div class="container-fluid p-3 review-widget-page">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="h3 my-0 mx-0 text-foreground">Review Widget</h3>
        <p class="text-muted-foreground text-sm">Manage your review widgets and installations</p>
      </div>
      <div class="flex items-center space-x-4">
        <button
          class="btn round-xl px-4 create-customer-btn"
          @click="showCreateCustomerModal = true"
        >
          Create Customer
        </button>
        <button
          class="btn round-xl px-4 create-widget-btn"
          @click="createWidgetGuardian"
        >
          <i class="uil uil-plus"></i>
          <span class="text-sm font-medium"> Create Widget</span>
        </button>
        <router-link
          v-if="user && user.business"
          :to="{
            name: 'request-reviews',
            params: {
              /* eslint-disable-next-line vue/camelcase */
              unique_id: user.business.unique_id + '-' + user.business.id,
            },
          }"
          class="btn bg-primary space-x-3 round-xl px-4 request-review-btn"
          target="_blank"
          ><i class="uil uil-star"></i
          ><span class="text-sm font-medium"> Request Review</span>
        </router-link>
      </div>
    </div>
    <hr class="pb-4 pt-0" style="margin: 0 -40px 0 -20px !important" />
    <!-- Main Content -->
    <div class="space-y-6">
      <!-- Tab Navigation -->
      <div class="tabs-container">
        <div class="tabs-list">
          <button
            type="button"
            role="tab"
            class="sub-tab-button"
            :class="{ active: activeTab === 'active' }"
            @click="switchTab('active')"
          >
            Active
          </button>
          <button
            type="button"
            role="tab"
            class="sub-tab-button"
            :class="{ active: activeTab === 'archived' }"
            @click="switchTab('archived')"
          >
            Archived
          </button>
        </div>

        <!-- Active Tab Content -->
        <div v-if="activeTab === 'active'" class="tab-content">
          <div class="table-card">
            <div class="table-wrapper">
              <table class="widgets-table">
                <thead>
                  <tr>
                    <th class="text-left">Widget Name</th>
                    <th class="text-left">Widget Type</th>
                    <th class="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loadingWidgets">
                    <td colspan="3" class="text-center py-4">
                      <b-spinner />
                    </td>
                  </tr>
                  <tr v-else-if="widgets.length === 0">
                    <td colspan="3" class="text-center py-4 text-muted">
                      No widgets found
                    </td>
                  </tr>
                  <tr
                    v-for="widget in widgets"
                    :key="widget.id"
                    class="table-row"
                  >
                    <td class="font-medium">{{ widget.business?.business_name || 'Main Review Widget' }}</td>
                    <td>
                      <div class="flex items-center space-x-2">
                        <div class="widget-type-indicator" :class="getWidgetTypeClass(widget.layout_style)"></div>
                        <span class="font-medium">{{ getWidgetTypeName(widget.layout_style) }}</span>
                      </div>
                    </td>
                    <td class="text-right">
                      <div class="flex justify-end space-x-2">
                        <button
                          class="action-button"
                          @click="addWidgetModal(widget)"
                        >
                          Add Widget
                        </button>
                        <button
                          class="action-button"
                          @click="addWidgetModal(widget, true)"
                        >
                          Add Popup
                        </button>
                        <a
                          :href="showLink(widget)"
                          target="_blank"
                          class="action-button"
                        >
                          Review Page
                        </a>
                        <button
                          class="action-button"
                          @click="editWidget(widget)"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="action-icon">
                            <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"></path>
                          </svg>
                          Edit Widget
                        </button>
                        <router-link
                          :to="{
                            name: 'business.business_reviews',
                            params: { business: widget.business?.id },
                          }"
                          class="action-button"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="action-icon">
                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"></path>
                            <circle cx="12" cy="12" r="3"></circle>
                          </svg>
                          See Reviews
                        </router-link>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Archived Tab Content -->
        <div v-if="activeTab === 'archived'" class="tab-content">
          <div class="table-card">
            <div class="table-wrapper">
              <table class="widgets-table">
                <thead>
                  <tr>
                    <th class="text-left">Widget Name</th>
                    <th class="text-left">Widget Type</th>
                    <th class="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loadingWidgets">
                    <td colspan="3" class="text-center py-4">
                      <b-spinner />
                    </td>
                  </tr>
                  <tr v-else-if="archivedWidgets.length === 0">
                    <td colspan="3" class="text-center py-4 text-muted">
                      No archived widgets found
                    </td>
                  </tr>
                  <tr
                    v-for="widget in archivedWidgets"
                    :key="widget.id"
                    class="table-row"
                  >
                    <td class="font-medium">{{ widget.business?.business_name || 'Main Review Widget' }}</td>
                    <td>
                      <div class="flex items-center space-x-2">
                        <div class="widget-type-indicator" :class="getWidgetTypeClass(widget.layout_style)"></div>
                        <span class="font-medium">{{ getWidgetTypeName(widget.layout_style) }}</span>
                      </div>
                    </td>
                    <td class="text-right">
                      <div class="flex justify-end space-x-2">
                        <button
                          class="action-button"
                          @click="restoreWidget(widget)"
                        >
                          Restore
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Total Count -->
      <div class="text-sm text-muted-foreground">
        Total {{ totalWidgets }} widgets
      </div>
    </div>

    <!-- Modals -->
    <b-modal
      v-model="showAddWidgetCode"
      :title="addCodeModalHeader"
      :hide-footer="true"
      size="lg"
      @hidden="editorHTML = null"
    >
      <h5>Copy and paste this code into desired place of your website</h5>
      <textarea
        v-model="editorHTML"
        class="form-control"
        rows="8"
      />
      <button
        class="btn btn-primary btn-sm mt-4"
        @click="copyWidgetCode(editorHTML)"
      >
        Copy Embed Code
      </button>
    </b-modal>

    <b-modal
      v-model="showCreateWidgetModal"
      size="lg"
      :hide-footer="true"
      :hide-header="true"
    >
      <div class="text-center">
        <h4>Create Widget</h4>
        <template v-if="user && user.role === 'Agency' && user.agency">
          <zensocial-generator
            ref="rlg"
            :search-text="widgetDefaultText"
            class="widget-creator"
            :agency-id="user.agency.id"
            :starting-step="1"
            header-text="Search For The Business"
            background-color="#ffffff"
            main-text-color="#222222"
            primary-button-background-color="#34a853"
            primary-button-text-color="#ffffff"
            :google-api-key="user.agency.landing_page?.admin_settings?.google_api_key"
            place-holder="Search"
            :manually-created="true"
            button-text="Generate My Widget"
            :force-busy="createIsBusy"
            @prospectCreated="prospectCreated"
          />
        </template>
        <template v-else-if="user && user.business">
          <p class="mb-3">
            Create a review widget for
            <strong>{{ user.business.business_name || 'your business' }}</strong>.
          </p>
          <b-button
            variant="primary"
            :disabled="createIsBusy || !user.business.location_id"
            @click="createLocationWidgetForBusiness"
          >
            <b-spinner v-if="createIsBusy" small class="mr-1" />
            Create Widget
          </b-button>
          <p v-if="!user.business.location_id" class="text-danger small mt-2 mb-0">
            Connect your Google Business Profile in Settings first.
          </p>
        </template>
      </div>
    </b-modal>

    <!-- Widget Editor Modal -->
    <b-modal
      v-model="showEditor"
      :hide-footer="true"
      body-class="edit-widget-modal-body-wrapper"
      header-class="align-items-center"
      modal-class="edit_widget_modal widget-editor widget-modal"
      size="lg"
    >
      <template v-slot:modal-header>
        <h5 class="text-white">
          <span v-if="editor.editingWidget.business">
            {{ editor.editingWidget.business.business_name }}
          </span>
        </h5>
        <div class="float-right">
          <button class="btn btn-default btn-sm mr-2" @click="showEditor = false">
            Cancel
          </button>
          <button class="btn btn-success btn-sm mr-2" @click="saveEditor(false)">
            Save
          </button>
          <button class="btn btn-success btn-sm" @click="saveEditor(true)">
            Save & Exit
          </button>
        </div>
      </template>
      <div class="container-fluid">
        <div v-if="editor.editingWidget" class="row">
          <div id="Navbar" class="sidebar">
            <div class="left-nav-side float-left">
              <button :class="{ active: editor.editing === 'layout' }" @click="displayLayout">
                <i class="uil uil-window-grid d-block"></i>
                Layout
              </button>
              <button :class="{ active: editor.editing === 'review' }" @click="editor.editing = 'review'">
                <i class="uil uil-comment-alt-heart d-block"></i>
                Review
              </button>
              <button :class="{ active: editor.editing === 'style' }" @click="editor.editing = 'style'">
                <i class="uil uil-brush-alt d-block"></i>
                Style
              </button>
              <button :class="{ active: editor.editing === 'popup' }" @click="displayPopup">
                <i class="uil uil-laptop d-block"></i>
                Popup
              </button>
              <button :class="{ active: editor.editing === 'review_gate' }" @click="displayReivewGate">
                <i class="uil uil-comment-alt-block d-block"></i>
                Page
              </button>
              <button :class="{ active: editor.editing === 'settings' }" @click="editor.editing = 'settings'">
                <i class="uil uil-cog d-block"></i>
                Settings
              </button>
            </div>
            <ul class="nav nav-sidebar">
              <li v-if="editor.editing === 'layout'" class="layout">
                <h4>Layout</h4>
                <ul class="list-unstyled">
                  <li>
                    <span class="style-selector">
                      <template v-for="layout in editor.layouts">
                        <input
                          :id="layout.name"
                          :key="layout.name"
                          v-model="editor.editingWidget.layout_style"
                          :value="layout.name"
                          type="radio"
                          class="btn-check"
                          autocomplete="off"
                        />
                        <label
                          :key="layout.name + 'label'"
                          class="btn"
                          :class="{ checked: editor.editingWidget.layout_style === layout.name }"
                          :for="layout.name"
                        >
                          <img :alt="layout.label" :src="'/images/widget-layouts/' + layout.name + '-icon.png'" />
                          <span>{{ layout.label }}</span>
                        </label>
                      </template>
                    </span>
                  </li>
                  <li v-if="editor.editingWidget.layout_style === 'grid'">
                    <span>Number of Rows</span>
                    <v-select
                      v-model="editor.editingWidget.layout_grid.rows"
                      :clearable="false"
                      taggable
                      :options="[1, 2, 3, 4, 5, 6, 7]"
                    />
                  </li>
                  <li v-if="editor.editingWidget.layout_style === 'list'">
                    <span>Number of Rows</span>
                    <v-select
                      v-model="editor.editingWidget.layout_list.total_to_show"
                      :clearable="false"
                      taggable
                      :options="[1, 2, 3, 4, 5, 6, 7]"
                    />
                  </li>
                  <li v-if="editor.editingWidget.layout_style === 'slider'">
                    <span>Number of Columns</span>
                    <v-select
                      v-model="editor.editingWidget.layout_slider.columns"
                      :clearable="false"
                      taggable
                      :options="[1, 2, 3, 4]"
                    />
                  </li>
                  <li v-if="editor.editingWidget.layout_style === 'masonry'">
                    <span>Number To Show</span>
                    <v-select
                      v-model="editor.editingWidget.layout_masonry.total_to_show"
                      :clearable="false"
                      taggable
                      :options="[1, 2, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]"
                    />
                  </li>
                  <li v-if="!editor.editingWidget.layout_style !== 'popup'">
                    <span>Include Title</span>
                    <ToggleButton
                      v-model="editor.editingWidget.include_title"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li v-if="editor.editingWidget.include_title" class="d-block">
                    <span>Title</span>
                    <b-form-input v-model="editor.editingWidget.title" class="d-block mt-2" />
                  </li>
                  <li>
                    <span>Review Header</span>
                    <ToggleButton
                      v-model="editor.editingWidget.show_review_header"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Write a Review</span>
                    <ToggleButton
                      v-model="editor.editingWidget.show_write_review"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Full Width</span>
                    <ToggleButton
                      v-model="editor.editingWidget.full_width"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li v-if="!editor.editingWidget.full_width" class="d-block">
                    <vue-slider
                      v-model="editor.editingWidget.defined_width"
                      :lazy="true"
                      color="#1494ff"
                      tooltip="none"
                      :height="10"
                      :min="700"
                      :max="1920"
                    ></vue-slider>
                    <div class="d-block text-center">
                      Width: {{ editor.editingWidget.defined_width }}px
                    </div>
                  </li>
                </ul>
              </li>
              <li v-if="editor.editing === 'review'" class="review">
                <h4>Reviews</h4>
                <ul class="list-unstyled">
                  <li>
                    <span>Show Author Name</span>
                    <ToggleButton
                      v-model="editor.editingWidget.review_style.show_author_name"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Show Author Picture</span>
                    <ToggleButton
                      v-model="editor.editingWidget.review_style.show_author_picture"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Show Date</span>
                    <ToggleButton
                      v-model="editor.editingWidget.review_style.show_date"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Show Rating</span>
                    <ToggleButton
                      v-model="editor.editingWidget.review_style.show_rating"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Show Review Source</span>
                    <ToggleButton
                      v-model="editor.editingWidget.review_style.show_source"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                </ul>
              </li>
              <li v-if="editor.editing === 'style'" class="layout">
                <h4>Layout</h4>
                <ul class="list-unstyled">
                  <li class="d-inline-block text-center">
                    <span class="mb-2 d-block">Color Scheme</span>
                    <ToggleButton
                      v-model="editor.editingWidget.settings.color_scheme"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                      @change="updateColorScheme"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Font</span>
                    <v-select
                      v-model="editor.editingWidget.settings.font"
                      :searchable="false"
                      :clearable="false"
                      :options="editor.fonts"
                      label="frontend_label"
                    >
                      <template v-slot:option="{ frontend_label, font_family }">
                        <span :style="'font-family:' + font_family">{{ frontend_label }}</span>
                      </template>
                    </v-select>
                  </li>
                  <li>
                    <span>Background Color</span>
                    <PopperColor
                      v-model="editor.editingWidget.appearance_review.background_color"
                      title="Background Color"
                    />
                  </li>
                  <li>
                    <span>Text Color</span>
                    <PopperColor
                      v-model="editor.editingWidget.appearance_review.text_color"
                      title="Text Color"
                    />
                  </li>
                  <li>
                    <span>Title Color</span>
                    <PopperColor
                      v-model="editor.editingWidget.appearance_review.title_color"
                      title="Title Color"
                    />
                  </li>
                  <li>
                    <span>Text Font Size</span>
                    <v-select
                      v-model="editor.editingWidget.appearance_review.font_size"
                      :clearable="false"
                      taggable
                      :options="[10, 12, 14, 16, 18, 20, 22, 24, 26, 28]"
                    />
                  </li>
                  <li>
                    <span>Write A Review Background</span>
                    <PopperColor
                      v-model="editor.editingWidget.appearance_review.write_review_background_color"
                      title="Write Review Background Color"
                    />
                  </li>
                  <li>
                    <span>Write A Review Text</span>
                    <PopperColor
                      v-model="editor.editingWidget.appearance_review.write_review_text_color"
                      title="Write Review Text Color"
                    />
                  </li>
                  <li>
                    <span>Rating Color</span>
                    <PopperColor
                      v-model="editor.editingWidget.appearance_review.rating_color"
                      title="Rating Color"
                    />
                  </li>
                  <li>
                    <span>Corner Radius</span>
                    <div class="radius-selector">
                      <template v-for="radius in editor.border_radius">
                        <input
                          :id="radius.image"
                          :key="radius.value"
                          v-model="editor.editingWidget.appearance_review.border_radius"
                          :value="radius.value"
                          type="radio"
                          class="btn-check"
                          autocomplete="off"
                          name="border-radius"
                        />
                        <label
                          :key="radius.image + 'label'"
                          class="btn"
                          :class="{ checked: editor.editingWidget.appearance_review.border_radius === radius.value }"
                          :for="radius.image"
                        >
                          <img :alt="radius.value" :src="'/images/review-corners/' + radius.image + '.png'" />
                        </label>
                      </template>
                    </div>
                  </li>
                  <li>
                    <span>Padding</span>
                    <v-select
                      v-model="editor.editingWidget.appearance_review.gap"
                      :clearable="false"
                      taggable
                      :options="[10, 12, 15, 20, 25, 30]"
                    />
                  </li>
                </ul>
              </li>
              <li v-if="editor.editing === 'popup'" class="popup">
                <h4>Popup</h4>
                <ul class="list-unstyled">
                  <li class="d-inline-block text-center">
                    <span class="mb-2 d-block">Position</span>
                    <ToggleButton
                      v-model="editor.editingWidget.layout_floating_rotator.position"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Delay</span>
                    <v-select
                      v-model="editor.editingWidget.layout_floating_rotator.delay"
                      class="rotator-delay"
                      taggable
                      :clearable="false"
                      :options="[5, 6, 7, 8, 9]"
                    >
                    </v-select>
                  </li>
                </ul>
              </li>
              <li v-if="editor.editing === 'review_gate'" class="reviewgate">
                <h4>Review Gate</h4>
                <ul class="list-unstyled">
                  <li>
                    <b-form-input
                      v-model="editor.editingWidget.business.review_gate_settings.body_title"
                      class="w-100"
                    />
                  </li>
                  <li>
                    <div class="w-100">
                      <label class="d-block">Body Text</label>
                      <b-form-textarea
                        v-model="editor.editingWidget.business.review_gate_settings.body_text"
                        rows="6"
                        class="w-100"
                      />
                    </div>
                  </li>
                  <li>
                    <b-form-input
                      v-model="editor.editingWidget.business.review_gate_settings.good_label"
                      class="w-100"
                    />
                  </li>
                  <li>
                    <b-form-input
                      v-model="editor.editingWidget.business.review_gate_settings.bad_label"
                      class="w-100"
                    />
                  </li>
                </ul>
              </li>
              <li v-if="editor.editing === 'settings'" class="settings">
                <h4>Settings</h4>
                <ul class="list-unstyled">
                  <li>
                    <span>Reviews Only With Text</span>
                    <ToggleButton
                      v-model="editor.editingWidget.settings.show_only_text_reviews"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Minimum Rating</span>
                    <v-select
                      v-model="editor.editingWidget.settings.min_rating"
                      class="min-rating"
                      :clearable="false"
                      :options="[5, 4, 3, 2, 1]"
                    >
                    </v-select>
                  </li>
                  <li>
                    <span>Schema.org</span>
                    <ToggleButton
                      v-model="editor.editingWidget.settings.enable_schema"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Enable External Links</span>
                    <ToggleButton
                      v-model="editor.editingWidget.settings.enable_external_links"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Open Links In New Window</span>
                    <ToggleButton
                      v-model="editor.editingWidget.settings.open_links_in_new_window"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                  <li>
                    <span>Gate Reviews</span>
                    <ToggleButton
                      v-model="editor.editingWidget.settings.gate_review"
                      :height="20"
                      :width="45"
                      color="#00b050"
                      :sync="true"
                    ></ToggleButton>
                  </li>
                </ul>
              </li>
            </ul>
          </div>
          <div class="editor-content">
            <review-gate
              v-if="editor.editing === 'review_gate'"
              :business="editor.editingWidget"
            />
            <iframe
              v-else
              id="preview-frame"
              class="w-full h-full"
              :srcdoc="iframeSrc"
              frameborder="0"
              :style="'background-color:' + editor.background_color + ';'"
            ></iframe>
          </div>
        </div>
      </div>
    </b-modal>

    <b-modal
      v-model="showCreateCustomerModal"
      title="Create Customer"
      :hide-footer="true"
    >
      <p class="mb-3">Create Customer is managed from your Customers screen.</p>
      <b-button variant="primary" @click="goToCreateCustomer">
        Go To Create Customer
      </b-button>
    </b-modal>
  </div>
</template>

<script>
import { BSpinner, BModal, BFormGroup, BFormInput } from 'bootstrap-vue'
import Vue from 'vue'
import { ToggleButton } from 'vue-js-toggle-button'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import PopperColor from '@components/Common/PopperColor'
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'
import ReviewGate from '@components/Business/ReviewGate'
import ZensocialGenerator from '../../../../embed-form/src/components/Form'
import '../../../design/custom/components/_dashboard.scss'
import './ReviewWidgetEditorGlobal.scss'

export default {
  name: 'ReviewWidget',
  components: {
    BSpinner,
    BModal,
    BFormGroup,
    BFormInput,
    ToggleButton,
    'v-select': vSelect,
    PopperColor,
    VueSlider,
    'review-gate': ReviewGate,
    'zensocial-generator': ZensocialGenerator,
  },
  data() {
    return {
      activeTab: 'active',
      loadingWidgets: false,
      showAddWidgetCode: false,
      editorHTML: null,
      addCodeModalHeader: 'Add Widget Code',
      showCreateWidgetModal: false,
      createIsBusy: false,
      widgetDefaultText: '',
      creatingWidget: false,
      showCreateCustomerModal: false,
      gettingWidget: false,
      showEditor: false,
      editor: {
        background_color: '#ffffff',
        has_loaded: false,
        editing: 'layout',
        editingWidget: {
          layout_style: null,
          widget_style: null,
          gate_body_title: '',
          gate_body_text: '',
          gate_good_label: '',
          gate_bad_label: '',
          business: null,
        },
        layouts: [
          { label: 'Grid', name: 'grid' },
          { label: 'Masonry', name: 'masonry' },
          { label: 'List', name: 'list' },
          { label: 'Carousel', name: 'slider' },
        ],
        border_radius: [
          { image: 'square', value: 0 },
          { image: 'rounded', value: 8 },
          { image: 'rounded-heavy', value: 20 },
        ],
        fonts: [
          {
            backend_name: 'system',
            frontend_label: 'Apply From Website',
            href: null,
            font_family: null,
          },
          {
            backend_name: 'Montserrat',
            frontend_label: 'Montserrat',
            href: 'https://fonts.googleapis.com/css2?family=Montserrat&family=Roboto:wght@300;400&display=swap',
            font_family: "'Montserrat', sans-serif;",
          },
          {
            backend_name: 'Nunito',
            frontend_label: 'Nunito',
            href: 'https://fonts.googleapis.com/css2?family=Nunito&display=swap',
            font_family: "'Nunito', sans-serif;",
          },
          {
            backend_name: 'Oswald',
            frontend_label: 'Oswald',
            href: 'https://fonts.googleapis.com/css2?family=Oswald&display=swap',
            font_family: "'Oswald', sans-serif",
          },
          {
            backend_name: 'Roboto',
            frontend_label: 'Roboto',
            href: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400&display=swap',
            font_family: "'Roboto', sans-serif",
          },
        ],
      },
      datatable: {
        queries: {
          per_page: 10,
          page: 1,
          status: 'active',
          search: null,
          sort_by: 'id',
          sort_desc: true,
          business_id: null,
          created_from_business: true,
        },
      },
    }
  },
  computed: {
    widgets() {
      if (this.activeTab === 'active') {
        try {
          const widgetsGetter = this.$store.getters['widgets/all']
          return Array.isArray(widgetsGetter) ? widgetsGetter : []
        } catch (error) {
          console.error('Error accessing widgets getter:', error)
          return []
        }
      }
      return []
    },
    archivedWidgets() {
      if (this.activeTab === 'archived') {
        try {
          const widgetsGetter = this.$store.getters['widgets/all']
          return Array.isArray(widgetsGetter) ? widgetsGetter : []
        } catch (error) {
          console.error('Error accessing widgets getter:', error)
          return []
        }
      }
      return []
    },
    totalWidgets() {
      try {
        const total = this.$store.getters['widgets/total']
        return typeof total === 'number' ? total : 0
      } catch (error) {
        console.error('Error accessing widgets total getter:', error)
        return 0
      }
    },
    user() {
      return this.$store.getters['auth/user']
    },
    iframeSrc() {
      const appDomain =
        process.env.NODE_ENV === 'development'
          ? 'https://review-reply-app.dev/widget/dist/v2/index.43e96d9d.js'
          : `${process.env.VUE_APP_GOOGLE_WIDGET_URI}widget/dist/v2/index.js`
      return (
        '<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Roboto&display=swap" />' +
        '\n' +
        '<scr' +
        'ipt type="module" src="' +
        appDomain +
        '"></scr' +
        'ipt>' +
        `<div style="font-family: 'Roboto', sans-serif;" class="preview"><div class="preview-widget"><div class="rwg-app-${this.editor.editingWidget.uuid}" id="rwg-preview"></div></div></div>`
      )
    },
    settingsString() {
      return JSON.stringify(this.editor.editingWidget)
    },
    layoutStyle() {
      return this.editor.editingWidget.layout_style
    },
  },
  watch: {
    user: {
      handler(val) {
        if (val && val.business && this.datatable) {
          this.datatable.queries.business_id = val.business.id
          this.getWidgets()
        }
      },
      deep: true,
    },
    layoutStyle(v) {
      this.editor.editingWidget.widget_style = v
    },
    settingsString(value) {
      const elem = document.getElementById('preview-frame')
      if (elem !== null && this.editor.has_loaded) {
        const jsonSettings = JSON.parse(value)
        jsonSettings.reviews = []
        jsonSettings.review_style.show_read_more_button =
          jsonSettings.review_style.show_read_more_button === 'true'
        elem.contentWindow.document
          .getElementById('rwg-preview')
          .dispatchEvent(
            new CustomEvent('settings-changed', {
              detail: {
                settings: jsonSettings,
                items: [],
                widget_style: this.editor.editingWidget.widget_style,
              },
            })
          )
      }
    },
  },
  created() {
    const pageKey = this.activeTab === 'active' ? 'index' : this.activeTab
    this.$store.dispatch('widgets/setCurrentPage', pageKey)
  },
  mounted() {
    if (this.user && this.user.business) {
      this.datatable.queries.business_id = this.user.business.id
      this.getWidgets()
    }

    this.editor.fonts.forEach((font) => {
      if (font.href !== null) {
        const thisFont = document.createElement('link')
        thisFont.setAttribute('rel', 'stylesheet')
        thisFont.setAttribute('href', font.href)
        document.head.appendChild(thisFont)
      }
    })
    this.editor.has_loaded = true

    if (window.location.search.includes('create_widget')) {
      this.$store
        .dispatch('widgets/canCreate')
        .then((response) => {
          if (response.success) {
            this.showCreateWidgetModal = true
          } else {
            response.messages.forEach((message) => {
              Vue.$toast.open({
                message,
                type: 'error',
                duration: 15000,
              })
            })
          }
          const query = Object.assign({}, this.$route.query)
          delete query.create_widget
          if (Object.prototype.hasOwnProperty.call(query, 'business_name')) {
            this.widgetDefaultText = decodeURI(query.business_name)
            delete query.business_name
          }
          this.$router.replace({ query })
        })
        .catch((err) => {
          console.log(err)
        })
    }
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab
      this.datatable.queries.status = tab === 'active' ? 'active' : 'archived'
      const pageKey = tab === 'active' ? 'index' : 'archived'
      this.$store.dispatch('widgets/setCurrentPage', pageKey)
      this.getWidgets(pageKey)
    },
    getWidgets(pageKey) {
      const vuexPage = pageKey || (this.activeTab === 'active' ? 'index' : 'archived')
      this.loadingWidgets = true
      this.$store
        .dispatch('widgets/getAll', {
          queries: this.datatable.queries,
          page: vuexPage,
        })
        .then(() => {
          this.loadingWidgets = false
        })
        .catch(() => {
          this.loadingWidgets = false
        })
    },
    getWidgetTypeName(layoutStyle) {
      const types = {
        masonry: 'Masonry',
        grid: 'Grid',
        list: 'List',
        slider: 'Carousel',
        floating_rotator: 'Popup',
      }
      return types[layoutStyle] || 'Masonry'
    },
    getWidgetTypeClass() {
      return 'bg-green-500'
    },
    showLink(widget) {
      const isStaging = typeof window !== 'undefined' && window.location.origin.includes('staging.')
      const appDomain = isStaging ? 'https://staging.replyengine.io/review/' : 'https://u.reviewour.biz/review/'
      const widgetId = widget.business?.unique_id ?? this.user?.business?.unique_id
      return `${appDomain}${widgetId || ''}`
    },
    addWidgetModal(widget, isFloating = false) {
      let widgetDiv = `<div class="rwg-app-${widget.uuid}"></div>`
      this.addCodeModalHeader = 'Add Widget Code'
      if (isFloating) {
        this.addCodeModalHeader = 'Add Popup Code'
        widgetDiv = `<div class="rwg-app-${widget.uuid} floating"></div>`
      }
      const scriptUrl = process.env.VUE_APP_GOOGLE_WIDGET_URI + 'widget/dist/v2/index.js'
      this.editorHTML =
        '<!-- Start Review Widget Generator -->' +
        '\n' +
        widgetDiv +
        '\n' +
        '<scr' +
        'ipt src="' +
        scriptUrl +
        '"></s' +
        'cript>' +
        '\n' +
        '<!-- End Review Widget Generator  -->'
      this.showAddWidgetCode = true
    },
    copyWidgetCode(code) {
      navigator.clipboard.writeText(code.replaceAll('&lt;', '<'))
      Vue.$toast.open({
        message: 'Code Copied',
        type: 'success',
        duration: 5000,
      })
    },
    createWidgetGuardian() {
      this.creatingWidget = true
      this.$store
        .dispatch('widgets/canCreate')
        .then((response) => {
          this.creatingWidget = false
          if (response.success) {
            this.showCreateWidgetModal = true
          } else {
            response.messages.forEach((message) => {
              Vue.$toast.open({
                message,
                type: 'error',
                duration: 15000,
              })
            })
          }
        })
        .catch(() => {
          this.creatingWidget = false
        })
    },
    prospectCreated(business) {
      const businessId =
        this.user?.role === 'Business' && this.user?.business?.id ? this.user.business.id : business?.id
      if (!businessId) {
        Vue.$toast.open({
          message: 'Could not determine your business. Please refresh and try again.',
          type: 'error',
          duration: 5000,
        })
        return
      }
      this.createIsBusy = true
      this.$store
        .dispatch('widgets/create', { business_id: businessId })
        .then(() => {
          this.showCreateWidgetModal = false
          this.createIsBusy = false
          this.getWidgets()
        })
        .catch(() => {
          this.showCreateWidgetModal = false
          this.createIsBusy = false
        })
    },
    createLocationWidgetForBusiness() {
      if (!this.user?.business?.location_id || !this.user?.business?.id) {
        Vue.$toast.open({
          message: 'Connect your Google Business Profile in Settings first.',
          type: 'error',
          duration: 5000,
        })
        return
      }
      this.createIsBusy = true
      this.$store
        .dispatch('widgets/create_location_widget', {
          location_id: this.user.business.location_id,
          business_id: this.user.business.id,
        })
        .then(() => {
          this.showCreateWidgetModal = false
          this.createIsBusy = false
          this.getWidgets()
          Vue.$toast.open({
            message: 'Widget created successfully',
            type: 'success',
            duration: 5000,
          })
        })
        .catch((err) => {
          this.createIsBusy = false
          Vue.$toast.open({
            message: err.response?.data?.message || 'Failed to create widget',
            type: 'error',
            duration: 5000,
          })
        })
    },
    editWidget(widget) {
      this.getWidget(widget)
    },
    getWidget(widget) {
      if (!widget || !widget.id) {
        return
      }
      this.gettingWidget = true
      this.$store
        .dispatch('widgets/find', widget.id)
        .then((res) => {
          const found = res
          if (found.review_style && typeof found.review_style.show_read_more_button !== 'undefined') {
            found.review_style.show_read_more_button =
              found.review_style.show_read_more_button.toString()
          }
          if (found.appearance_review && !Object.prototype.hasOwnProperty.call(found.appearance_review, 'title_color')) {
            found.appearance_review.title_color = found.appearance_review.text_color
          }
          this.editor.editingWidget = found
          this.editor.editingWidget.widget_style = found.layout_style
          this.showEditor = true
        })
        .catch(() => {})
        .finally(() => {
          this.gettingWidget = false
        })
    },
    saveEditor(shouldClose = false) {
      this.$store
        .dispatch('widgets/update', this.editor.editingWidget)
        .then(() => {
          if (shouldClose) {
            this.showEditor = false
          }
        })
        .catch(() => {
          if (shouldClose) {
            this.showEditor = false
          }
        })
    },
    updateColorScheme() {
      if (!this.editor.editingWidget || !this.editor.editingWidget.settings) {
        return
      }
      if (this.editor.editingWidget.settings.color_scheme === 'dark') {
        this.editor.editingWidget.appearance_review.text_color = '#fff'
        this.editor.editingWidget.appearance_review.title_color = '#fff'
        this.editor.editingWidget.appearance_review.background_color = '#2a2a2a'
        this.editor.background_color = '#171717'
      } else {
        this.editor.editingWidget.appearance_review.text_color = '#000000'
        this.editor.editingWidget.appearance_review.title_color = '#000000'
        this.editor.editingWidget.appearance_review.background_color = '#f2f2f2'
        this.editor.background_color = '#ffffff'
      }
      this.editor.editingWidget.appearance_review.write_review_text_color = '#ffffff'
      this.editor.editingWidget.appearance_review.write_review_background_color = '#4085f2'
    },
    displayLayout() {
      this.editor.editingWidget.widget_style = this.editor.editingWidget.layout_style
      this.editor.editing = 'layout'
    },
    displayPopup() {
      this.editor.editingWidget.widget_style = 'floating_rotator'
      this.editor.editing = 'popup'
    },
    displayReivewGate() {
      this.editor.editing = 'review_gate'
    },
    restoreWidget(widget) {
      this.$store
        .dispatch('widgets/restore', widget.id)
        .then(() => {
          this.getWidgets()
          Vue.$toast.open({
            message: 'Widget restored',
            type: 'success',
            duration: 5000,
          })
        })
        .catch(() => {})
    },
    goToCreateCustomer() {
      this.showCreateCustomerModal = false
      const isAgencyLogin = !!this.user?.agency
      const isBusinessWithAgencyAccess =
        this.user?.role === 'Business' && !!this.user?.business?.owner?.agency
      if (isAgencyLogin || isBusinessWithAgencyAccess) {
        this.$router.push({
          name: 'new-agency.businesses.index',
          query: { create_customer: true },
        })
        return
      }

      Vue.$toast.open({
        message: 'Create Customer is available on the Agency Customers page.',
        type: 'info',
        duration: 5000,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.review-widget-page {
  min-height: 100vh;
  background: white;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.justify-between {
  justify-content: space-between;
}

.justify-end {
  justify-content: flex-end;
}

.space-x-2 {
  gap: 0.5rem;
}

.space-x-3 {
  gap: 0.75rem;
}

.space-y-6 {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

// Header Styles
.h3 {
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.2;
}

.text-foreground {
  color: #1f2937;
}

.text-muted-foreground {
  color: #6b7280;
  font-size: 0.875rem;
}

.text-sm {
  font-size: 0.875rem;
}

.font-medium {
  font-weight: 500;
}

// Buttons
.create-customer-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  height: 2.5rem;
  padding: 0 1rem;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
    color: #1f2937;
  }
}

.create-widget-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  border: 1px solid #d1d5db;
  background-color: transparent;
  color: #374151;
  transition: all 0.2s;
  cursor: pointer;

  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
    color: #1f2937;
  }
}

.request-review-btn {
  background-color: rgb(36, 99, 235) !important;
  color: #ffffff !important;
}

// Tabs
.tabs-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.tabs-list {
  display: inline-flex;
  height: 40px;
  align-items: center;
  justify-content: flex-start;
  border-radius: 6px;
  background: #f1f5f9;
  padding: 4px;
  color: #6b7280;
  width: fit-content;
}

.sub-tab-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  border-radius: 4px;
  padding: 6px 12px;
  font-size: 14px;
  font-weight: 500;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s;
  outline: none;

  &:focus-visible {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }

  &:disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &.active {
    background: white;
    color: #1f2937;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}

.tab-content {
  margin-top: 0.5rem;
}

// Table
.table-card {
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
  background-color: white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow: auto;
}

.widgets-table {
  width: 100%;
  font-size: 0.875rem;
  border-collapse: collapse;

  thead {
    tr {
      border-bottom: 1px solid #e5e7eb;
    }
  }

  th {
    height: 3rem;
    padding: 0 1rem;
    text-align: left;
    font-weight: 500;
    color: #6b7280;
    vertical-align: middle;

    &.text-right {
      text-align: right;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e5e7eb;
      transition: background-color 0.2s;

      &:last-child {
        border-bottom: 0;
      }

      &:hover {
        background-color: #f9fafb;
      }
    }
  }

  td {
    padding: 1rem;
    vertical-align: middle;

    &.text-right {
      text-align: right;
    }
  }
}

.widget-type-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid;
  flex-shrink: 0;

  &.bg-green-500 {
    background-color: #10b981;
    border-color: #10b981;
  }
}

.action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  white-space: nowrap;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  height: 2.25rem;
  border-radius: 0.375rem;
  padding: 0 0.75rem;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background-color: #f9fafb;
    color: #1f2937;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 2px solid transparent;
    outline-offset: 2px;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  .action-icon {
    width: 16px;
    height: 16px;
    margin-right: 0.25rem;
    flex-shrink: 0;
    pointer-events: none;
  }
}
</style>


<style>
/* Google Places dropdown must sit above Bootstrap modals (z ~1050–1060) */
.pac-container,
.pac-container.pac-logo {
  z-index: 10000000 !important;
}
</style>
